import axios from 'axios';
import React, { useContext, useEffect, useReducer, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';
import { EnrollModal } from './DeliveryScreen';
import StaffPermissionsModal from '../../component/StaffPermissionsModal';
import '../../assets/ops.css';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        users: action.payload.products,
        totalUsers: action.payload.countProducts,
        totalPages: action.payload.pages,
        currentPage: action.payload.page,
        // The search and date-filter endpoints do not return role counts, so
        // keep the last known values rather than blanking the tab badges.
        roleCounts: action.payload.roleCounts || state.roleCounts,
        loading: false,
        error: '',
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

// A user can hold more than one staff flag, so show every role that applies
// rather than guessing which one matters most.
function RoleBadge({ user }) {
  const roles = [
    user.isAdmin && { label: 'Admin', bg: '#f8d7da', color: '#842029' },
    user.isAgent && { label: 'Agent', bg: '#cff4fc', color: '#055160' },
    user.isUserManager && { label: 'Manager', bg: '#e2d9f3', color: '#432874' },
    user.isProductManager && { label: 'Product Mgr', bg: '#cfe2ff', color: '#084298' },
  ].filter(Boolean);

  if (roles.length === 0) {
    roles.push({ label: 'User', bg: '#e9ecef', color: '#495057' });
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {roles.map((r) => (
        <span key={r.label} style={{ ...styles.roleBadge, background: r.bg, color: r.color }}>
          {r.label}
        </span>
      ))}
    </div>
  );
}

export default function ProductListScreen() {
  const markedUsers = useRef([]);
  const [{ loading, users, totalUsers, totalPages, currentPage, error, roleCounts }, dispatch] = useReducer(reducer, {
    loading: true,
    users: [],
    totalUsers: 0,
    totalPages: 0,
    currentPage: 1,
    error: '',
    roleCounts: {},
  });

  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;
  // Who may open the staff role/permission editor: admins, user managers, or
  // anyone explicitly granted canManageStaff. The server re-checks this and
  // every per-target rule, so this only decides which button shows.
  const canManageStaff = Boolean(
    userInfo?.isAdmin || userInfo?.isUserManager || userInfo?.permissions?.canManageStaff
  );
  // The broader user-administration actions — block, reset password, disable,
  // edit profile — stay with admins and full User Managers. A plain member
  // holding only canManageStaff sees the roster and the role editor, nothing
  // destructive. (Servers still re-check every one of these.)
  const canAdminUsers = Boolean(userInfo?.isAdmin || userInfo?.isUserManager);
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const page = parseInt(sp.get('page')) || 1;

  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('username');
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalMarked, setTotalMarked] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [activeSearchMode, setActiveSearchMode] = useState(null); // tracks what loaded the current data

  // Fetch users by filter type
  const fetchData = useCallback(async (filterValue) => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const { data } = await axios.get(
        api + `/api/users/admin?page=${page}&users=${filterValue}`,
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
      setActiveSearchMode(null);
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  }, [page, userInfo.token]);

  // Search users
  const doSearch = useCallback(async () => {
    if (!searchValue.trim()) {
      fetchData(activeFilter);
      return;
    }
    try {
      setSearchLoading(true);
      dispatch({ type: 'FETCH_REQUEST' });
      const { data } = await axios.get(
        api + `/api/users/admin/search?page=${page}&value=${encodeURIComponent(searchValue.trim())}&type=${searchType}&match=exact`,
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
      setActiveSearchMode('search');
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      toast.error(getError(err));
    } finally {
      setSearchLoading(false);
    }
  }, [searchValue, searchType, page, userInfo.token, activeFilter, fetchData]);

  // Date filter
  const filterByDate = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const { data } = await axios.post(
        api + `/api/users/filterUsersByDate/filter?page=${page}`,
        { startDate, endDate },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
      setActiveSearchMode('date');
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  }, [startDate, endDate, page, userInfo.token]);

  useEffect(() => {
    if (activeSearchMode === 'search') {
      doSearch();
    } else if (activeSearchMode === 'date') {
      filterByDate();
    } else {
      fetchData(activeFilter);
    }
  }, [page]);

  // Initial load
  useEffect(() => {
    fetchData('all');
  }, []);

  // Mark/unmark user
  function toggleMark(userId) {
    const idx = markedUsers.current.indexOf(userId);
    if (idx > -1) {
      markedUsers.current.splice(idx, 1);
    } else {
      markedUsers.current.push(userId);
    }
    setTotalMarked(markedUsers.current.length);
  }

  // Select/deselect all on current page
  function toggleSelectAll() {
    if (selectAll) {
      markedUsers.current = [];
      setTotalMarked(0);
    } else {
      markedUsers.current = users.map((u) => u._id);
      setTotalMarked(users.length);
    }
    setSelectAll(!selectAll);
  }

  // Bulk actions
  async function bulkAction(actionType) {
    if (markedUsers.current.length === 0) {
      toast.error('No users selected');
      return;
    }
    const endpoints = {
      block: '/api/users/admin/block',
      unblock: '/api/users/admin/unblock',
      delete: '/api/users/admin/disableuser',
    };
    const labels = { block: 'block', unblock: 'unblock', delete: 'disable' };
    if (!window.confirm(`Are you sure you want to ${labels[actionType]} ${markedUsers.current.length} user(s)?`)) return;
    try {
      const { data } = await axios.post(
        api + endpoints[actionType],
        { users: markedUsers.current },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      toast.success(data.message);
      markedUsers.current = [];
      setTotalMarked(0);
      setSelectAll(false);
      fetchData(activeFilter);
    } catch (err) {
      toast.error(getError(err));
    }
  }

  // Delete single user
  async function deleteUser(user) {
    if (!window.confirm(`Are you sure you want to delete "${user.name}"?`)) return;
    try {
      await axios.delete(api + `/api/users/${user._id}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      toast.success('User deleted successfully');
      fetchData(activeFilter);
    } catch (err) {
      toast.error(getError(err));
    }
  }

  // ── Password actions: show plain password / reset to a fresh 6-digit ──
  // Both backend endpoints (/admin/getpassword/:id and /admin/resetpassword)
  // are admin-gated. We display the result in a small modal so the admin can
  // copy it before sharing with the user.
  const [pwdModal, setPwdModal] = useState(null); // { user, password, mode: 'show'|'reset', loading }
  // Staff capabilities and fingerprint enrolment are managed straight from
  // this list — both are per-user and this is where an admin already is.
  const [permModal, setPermModal] = useState(null);
  const [fingerModal, setFingerModal] = useState(null);

  async function showUserPassword(user) {
    try {
      setPwdModal({ user, password: '', mode: 'show', loading: true });
      const { data } = await axios.get(api + `/api/users/admin/getpassword/${user._id}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      setPwdModal({ user, password: data.plainPassword || '(not stored — reset to view)', mode: 'show', loading: false });
    } catch (err) {
      setPwdModal(null);
      toast.error(getError(err));
    }
  }

  async function resetUserPassword(user) {
    if (!window.confirm(`Generate a new 6-digit password for "${user.name}"? Their current password will stop working immediately.`)) return;
    try {
      setPwdModal({ user, password: '', mode: 'reset', loading: true });
      const { data } = await axios.post(
        api + '/api/users/admin/resetpassword',
        { _id: user._id },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      setPwdModal({ user, password: data.password, mode: 'reset', loading: false });
    } catch (err) {
      setPwdModal(null);
      toast.error(getError(err));
    }
  }

  function copyPwdToClipboard() {
    if (!pwdModal?.password) return;
    navigator.clipboard.writeText(pwdModal.password).then(
      () => toast.success('Password copied'),
      () => toast.error('Copy failed')
    );
  }

  // Handle filter change
  function handleFilterChange(value) {
    setActiveFilter(value);
    setSearchValue('');
    setStartDate('');
    setEndDate('');
    setActiveSearchMode(null);
    navigate('/admin/users?page=1');
    fetchData(value);
  }

  // Handle search submit
  function handleSearchSubmit(e) {
    e.preventDefault();
    navigate('/admin/users?page=1');
    doSearch();
  }

  // Clear search
  function clearSearch() {
    setSearchValue('');
    setActiveSearchMode(null);
    navigate('/admin/users?page=1');
    fetchData(activeFilter);
  }

  // Clear date filter
  function clearDateFilter() {
    setStartDate('');
    setEndDate('');
    setActiveSearchMode(null);
    navigate('/admin/users?page=1');
    fetchData(activeFilter);
  }

  // Format date nicely
  function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // Get status badge
  function getStatusBadge(user) {
    if (!user.status) return <span style={styles.badge('#dc3545')}>Disabled</span>;
    if (user.isBlocked) return <span style={styles.badge('#dc3545')}>Blocked</span>;
    if (user.isAdmin) return <span style={styles.badge('#6f42c1')}>Admin</span>;
    if (user.isAgent) return <span style={styles.badge('#0d6efd')}>Agent</span>;
    if (user.isUserManager) return <span style={styles.badge('#0ea5e9')}>User Manager</span>;
    if (user.isActiveId) return <span style={styles.badge('#198754')}>Active</span>;
    return <span style={styles.badge('#6c757d')}>Inactive</span>;
  }

  // Pagination renderer
  function renderPagination() {
    const totalPagesNum = parseInt(totalPages) || 0;
    const currentPageNum = parseInt(page);
    if (totalPagesNum <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPagesNum, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    const linkBase = '/admin/users?page=';

    return (
      <div style={styles.paginationWrapper}>
        <div style={styles.paginationInfo}>
          Showing page <strong>{currentPageNum}</strong> of <strong>{totalPagesNum}</strong> ({totalUsers} users)
        </div>
        <nav>
          <ul style={styles.pagination}>
            <li>
              <Link
                to={`${linkBase}1`}
                style={{
                  ...styles.pageLink,
                  ...(currentPageNum <= 1 ? styles.pageLinkDisabled : {}),
                }}
                onClick={(e) => currentPageNum <= 1 && e.preventDefault()}
              >
                <i className="fa fa-angle-double-left" />
              </Link>
            </li>
            <li>
              <Link
                to={`${linkBase}${currentPageNum - 1}`}
                style={{
                  ...styles.pageLink,
                  ...(currentPageNum <= 1 ? styles.pageLinkDisabled : {}),
                }}
                onClick={(e) => currentPageNum <= 1 && e.preventDefault()}
              >
                <i className="fa fa-angle-left" /> Prev
              </Link>
            </li>
            {startPage > 1 && (
              <>
                <li>
                  <Link to={`${linkBase}1`} style={styles.pageLink}>1</Link>
                </li>
                {startPage > 2 && <li style={styles.pageEllipsis}>...</li>}
              </>
            )}
            {pages.map((p) => (
              <li key={p}>
                <Link
                  to={`${linkBase}${p}`}
                  style={{
                    ...styles.pageLink,
                    ...(p === currentPageNum ? styles.pageLinkActive : {}),
                  }}
                >
                  {p}
                </Link>
              </li>
            ))}
            {endPage < totalPagesNum && (
              <>
                {endPage < totalPagesNum - 1 && <li style={styles.pageEllipsis}>...</li>}
                <li>
                  <Link to={`${linkBase}${totalPagesNum}`} style={styles.pageLink}>{totalPagesNum}</Link>
                </li>
              </>
            )}
            <li>
              <Link
                to={`${linkBase}${currentPageNum + 1}`}
                style={{
                  ...styles.pageLink,
                  ...(currentPageNum >= totalPagesNum ? styles.pageLinkDisabled : {}),
                }}
                onClick={(e) => currentPageNum >= totalPagesNum && e.preventDefault()}
              >
                Next <i className="fa fa-angle-right" />
              </Link>
            </li>
            <li>
              <Link
                to={`${linkBase}${totalPagesNum}`}
                style={{
                  ...styles.pageLink,
                  ...(currentPageNum >= totalPagesNum ? styles.pageLinkDisabled : {}),
                }}
                onClick={(e) => currentPageNum >= totalPagesNum && e.preventDefault()}
              >
                <i className="fa fa-angle-double-right" />
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    );
  }

  // View user modal
  function renderViewModal() {
    if (!viewUser) return null;
    const u = viewUser;
    const fields = [
      { label: 'Full Name', value: u.name },
      { label: 'Username', value: u.username },
      { label: 'Email', value: u.email || '—' },
      { label: 'Phone', value: u.phone },
      { label: 'Gender', value: u.gender || '—' },
      { label: 'Date of Birth', value: u.dob || '—' },
      { label: 'Address', value: u.address || '—' },
      { label: 'NID', value: u.nid || '—' },
      { label: 'Reference', value: u.reference || '—' },
      { label: 'Placement', value: u.placement ? `${u.placement} (${u.position})` : '—' },
      { label: 'Designation', value: u.designation || '0' },
      { label: 'Points', value: u.point ?? 0 },
      { label: 'Available Balance', value: u.availablebalance ?? 0 },
      { label: 'Shopping Balance', value: u.shoppingbalance ?? 0 },
      { label: 'Left Team', value: u.leftuser ?? 0 },
      { label: 'Right Team', value: u.rightuser ?? 0 },
      { label: 'Active ID', value: u.isActiveId ? 'Yes' : 'No' },
      { label: 'Status', value: u.status ? 'Active' : 'Disabled' },
      { label: 'Blocked', value: u.isBlocked ? 'Yes' : 'No' },
      { label: 'Bank Name', value: u.bankname || '—' },
      { label: 'Bank Account', value: u.bankaccount || '—' },
      { label: 'Bank Branch', value: u.bankbranch || '—' },
      { label: 'Mobile Bank', value: u.mobilebankname || '—' },
      { label: 'Mobile Bank Acc.', value: u.mobilebankaccount || '—' },
      { label: 'Beneficiary', value: u.benificiaryname || '—' },
      { label: 'Created By', value: u.createdBy || '—' },
      { label: 'Joined', value: formatDate(u.createdAt) },
    ];

    return (
      <div style={styles.modalOverlay} onClick={() => setViewUser(null)}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.modalHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={styles.modalAvatar}>
                {u.profilePhoto && u.profilePhoto !== 'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png'
                  ? <img src={u.profilePhoto} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{(u.name || '?')[0].toUpperCase()}</span>
                }
              </div>
              <div>
                <h5 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1a1a2e' }}>{u.name}</h5>
                <span style={{ fontSize: 13, color: '#6c757d' }}>@{u.username}</span>
              </div>
            </div>
            <button onClick={() => setViewUser(null)} style={styles.modalCloseBtn}>&times;</button>
          </div>

          {/* Body */}
          <div style={styles.modalBody}>
            <div style={styles.modalGrid}>
              {fields.map((f, i) => (
                <div key={i} style={styles.modalField}>
                  <div style={styles.modalFieldLabel}>{f.label}</div>
                  <div style={styles.modalFieldValue}>{f.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div style={styles.modalFooter}>
            <Link to={`/admin/userreport/${u._id}`} style={{ ...styles.btn, ...styles.btnPrimary }}>
              <i className="fa fa-bar-chart" /> Full Report
            </Link>
            <Link to={`/admin/edituser/${u._id}`} style={{ ...styles.btn, ...styles.btnSuccess }}>
              <i className="fa fa-pencil" /> Edit User
            </Link>
            <button onClick={() => setViewUser(null)} style={{ ...styles.btn, ...styles.btnOutline }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <Helmet><title>Manage Users</title></Helmet>

      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div>
          <h4 style={styles.pageTitle}>Manage Users</h4>
          <p style={styles.pageSubtitle}>{totalUsers || 0} total users in the system</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={styles.filterRow}>
        {[
          { key: 'all', label: 'All Users', icon: 'fa-users' },
          { key: 'varified', label: 'Verified', icon: 'fa-check-circle' },
          { key: 'unvarified', label: 'Unverified', icon: 'fa-exclamation-circle' },
          { key: 'blocked', label: 'Blocked', icon: 'fa-ban' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => handleFilterChange(f.key)}
            style={{
              ...styles.filterTab,
              ...(activeFilter === f.key ? styles.filterTabActive : {}),
            }}
          >
            <i className={`fa ${f.icon}`} style={{ marginRight: 6 }} />
            {f.label}
          </button>
        ))}
      </div>

      {/* Role Tabs — active accounts only, same as All Users */}
      <div style={styles.filterRow}>
        {[
          { key: 'members', label: 'Users', icon: 'fa-user', count: roleCounts.members },
          { key: 'agents', label: 'Agents', icon: 'fa-handshake-o', count: roleCounts.agents },
          { key: 'managers', label: 'Managers', icon: 'fa-user-circle', count: roleCounts.managers },
          { key: 'admins', label: 'Admins', icon: 'fa-shield', count: roleCounts.admins },
          { key: 'staff', label: 'All Staff', icon: 'fa-users' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => handleFilterChange(f.key)}
            style={{
              ...styles.filterTab,
              ...(activeFilter === f.key ? styles.filterTabActive : {}),
            }}
          >
            <i className={`fa ${f.icon}`} style={{ marginRight: 6 }} />
            {f.label}
            {f.count !== undefined && (
              <span style={styles.tabCount}>{f.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div style={styles.card}>
        <form onSubmit={handleSearchSubmit} style={styles.searchRow}>
          <div style={styles.searchInputGroup}>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              style={styles.searchSelect}
            >
              <option value="username">Username</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="reference">Reference</option>
              <option value="placement">Placement</option>
              <option value="phone">Phone</option>
            </select>
            <input
              type="text"
              placeholder={`Search by ${searchType}... (exact match)`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={styles.searchInput}
            />
            {searchValue && (
              <button type="button" onClick={clearSearch} style={styles.clearBtn} title="Clear search">
                <i className="fa fa-times" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={searchLoading || !searchValue.trim()}
            style={{
              ...styles.btn,
              ...styles.btnPrimary,
              opacity: searchLoading || !searchValue.trim() ? 0.6 : 1,
            }}
          >
            {searchLoading ? (
              <><i className="fa fa-spinner fa-spin" /> Searching...</>
            ) : (
              <><i className="fa fa-search" /> Search</>
            )}
          </button>
        </form>

        {/* Date Range Filter */}
        <div style={styles.dateRow}>
          <div style={styles.dateGroup}>
            <label style={styles.dateLabel}>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
          <div style={styles.dateGroup}>
            <label style={styles.dateLabel}>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
          <button
            onClick={() => { navigate('/admin/users?page=1'); filterByDate(); }}
            disabled={!startDate || !endDate}
            style={{
              ...styles.btn,
              ...styles.btnSuccess,
              opacity: !startDate || !endDate ? 0.6 : 1,
            }}
          >
            <i className="fa fa-calendar" /> Filter by Date
          </button>
          {(startDate || endDate) && (
            <button onClick={clearDateFilter} style={{ ...styles.btn, ...styles.btnOutline }}>
              <i className="fa fa-times" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {canAdminUsers && totalMarked > 0 && (
        <div style={styles.bulkBar}>
          <span style={styles.bulkText}>
            <i className="fa fa-check-square" /> {totalMarked} user{totalMarked > 1 ? 's' : ''} selected
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => bulkAction('block')} style={{ ...styles.btn, ...styles.btnWarning }}>
              <i className="fa fa-ban" /> Block
            </button>
            <button onClick={() => bulkAction('unblock')} style={{ ...styles.btn, ...styles.btnInfo }}>
              <i className="fa fa-unlock" /> Unblock
            </button>
            <button onClick={() => bulkAction('delete')} style={{ ...styles.btn, ...styles.btnDanger }}>
              <i className="fa fa-trash" /> Disable
            </button>
          </div>
        </div>
      )}

      {/* Active search indicator */}
      {activeSearchMode === 'search' && searchValue && (
        <div style={styles.activeSearchBanner}>
          <span>
            <i className="fa fa-info-circle" /> Showing exact matches for <strong>"{searchValue}"</strong> in <strong>{searchType}</strong>
          </span>
          <button onClick={clearSearch} style={{ ...styles.btn, ...styles.btnOutlineSmall }}>
            <i className="fa fa-times" /> Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.loadingState}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: 28, color: '#0d6efd' }} />
            <p style={{ margin: '12px 0 0', color: '#6c757d' }}>Loading users...</p>
          </div>
        ) : error ? (
          <div style={styles.errorState}>
            <i className="fa fa-exclamation-triangle" style={{ fontSize: 28, color: '#dc3545' }} />
            <p style={{ margin: '12px 0 0', color: '#dc3545' }}>{error}</p>
          </div>
        ) : !users || users.length === 0 ? (
          <div style={styles.emptyState}>
            <i className="fa fa-users" style={{ fontSize: 40, color: '#dee2e6' }} />
            <p style={{ margin: '12px 0 0', color: '#6c757d', fontSize: 15 }}>No users found</p>
          </div>
        ) : (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        style={styles.checkbox}
                      />
                    </th>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th} className="hide-mobile">Email</th>
                    <th style={styles.th} className="hide-mobile">Phone</th>
                    <th style={styles.th} className="hide-tablet">Reference</th>
                    <th style={styles.th} className="hide-tablet">Placement</th>
                    <th style={styles.th} className="hide-mobile">Points / Bal</th>
                    <th style={styles.th} className="hide-tablet">Team</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th} className="hide-tablet">Joined</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user._id}
                      style={{
                        ...styles.tr,
                        ...(markedUsers.current.includes(user._id) ? styles.trSelected : {}),
                        ...(!user.status ? styles.trDisabled : {}),
                      }}
                    >
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={markedUsers.current.includes(user._id)}
                          onChange={() => toggleMark(user._id)}
                          value={user._id}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.td}>
                        <div style={styles.userCell}>
                          <div style={styles.userAvatar}>
                            {(user.name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={styles.userName}>{user.name}</div>
                            <div style={styles.userUsername}>@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <RoleBadge user={user} />
                      </td>
                      <td style={{ ...styles.td, color: '#495057', fontSize: 13 }} className="hide-mobile">
                        {user.email || '—'}
                      </td>
                      <td style={{ ...styles.td, fontSize: 13 }} className="hide-mobile">
                        {user.phone || '—'}
                      </td>
                      <td style={{ ...styles.td, fontSize: 13 }} className="hide-tablet">
                        {user.reference || '—'}
                      </td>
                      <td style={{ ...styles.td, fontSize: 13 }} className="hide-tablet">
                        {user.placement ? `${user.placement} (${user.position})` : '—'}
                      </td>
                      <td style={styles.td} className="hide-mobile">
                        <span style={styles.statValue}>{user.point ?? 0}</span>
                        <span style={{ color: '#adb5bd', margin: '0 2px' }}>/</span>
                        <span style={styles.statValue}>{user.availablebalance ?? 0}</span>
                      </td>
                      <td style={styles.td} className="hide-tablet">
                        <span style={{ fontSize: 13 }}>
                          L:{user.leftuser ?? 0} / R:{user.rightuser ?? 0}
                        </span>
                      </td>
                      <td style={styles.td}>{getStatusBadge(user)}</td>
                      <td style={{ ...styles.td, fontSize: 12, color: '#6c757d' }} className="hide-tablet">
                        {formatDate(user.createdAt)}
                        {user.createdBy && user.createdBy !== 'Verified User' && (
                          <div style={{ fontSize: 11, color: '#adb5bd' }}>by {user.createdBy}</div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionBtns}>
                          <button
                            onClick={() => setViewUser(user)}
                            style={styles.actionBtn}
                            title="View Details"
                          >
                            <i className="fa fa-eye" />
                          </button>
                          <Link
                            to={`/admin/userreport/${user._id}`}
                            style={styles.actionBtn}
                            title="View Report"
                          >
                            <i className="fa fa-bar-chart" />
                          </Link>
                          {userInfo.isAdmin && (
                            <a
                              href={`${window.location.origin}${window.location.pathname}#/admin/tree/${encodeURIComponent(user.username)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ ...styles.actionBtn, color: '#4f46e5' }}
                              title="Tree view — upline & downline (opens a new tab)"
                            >
                              <i className="fa fa-sitemap" />
                            </a>
                          )}
                          {canAdminUsers && (
                            <>
                              <Link
                                to={`/admin/edituser/${user._id}`}
                                style={styles.actionBtn}
                                title="Edit User"
                              >
                                <i className="fa fa-pencil" />
                              </Link>
                              <button
                                onClick={() => showUserPassword(user)}
                                style={styles.actionBtn}
                                title="Show Password"
                              >
                                <i className="fa fa-key" />
                              </button>
                              <button
                                onClick={() => resetUserPassword(user)}
                                style={{ ...styles.actionBtn, color: '#f59e0b' }}
                                title="Reset Password (generate new 6-digit)"
                              >
                                <i className="fa fa-refresh" />
                              </button>
                              <button
                                onClick={() => setFingerModal(user)}
                                style={{ ...styles.actionBtn, color: '#7c3aed' }}
                                title="Fingerprint enrolment"
                              >
                                <i className="fa fa-hand-paper-o" />
                              </button>
                            </>
                          )}
                          {canManageStaff && (
                            <button
                              onClick={() => setPermModal(user)}
                              style={{ ...styles.actionBtn, color: '#0ea5e9' }}
                              title="Staff roles & permissions"
                            >
                              <i className="fa fa-shield" />
                            </button>
                          )}
                          {userInfo.isAdmin && (
                            <button
                              onClick={() => deleteUser(user)}
                              style={{ ...styles.actionBtn, ...styles.actionBtnDanger }}
                              title="Delete User"
                            >
                              <i className="fa fa-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {renderPagination()}
          </>
        )}
      </div>

      {/* View User Modal */}
      {renderViewModal()}

      {/* Staff capabilities */}
      {permModal && (
        <StaffPermissionsModal
          user={permModal}
          token={userInfo.token}
          onClose={() => setPermModal(null)}
        />
      )}

      {/* Fingerprint enrolment */}
      {fingerModal && (
        <FingerprintModal
          user={fingerModal}
          token={userInfo.token}
          onClose={() => setFingerModal(null)}
        />
      )}

      {/* Password modal — shows current plaintext or freshly-reset password */}
      {pwdModal && (
        <div style={styles.modalOverlay} onClick={() => setPwdModal(null)}>
          <div
            style={{ ...styles.modalContent, maxWidth: 460 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <h5 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#1a1a2e' }}>
                  <i
                    className={`fa ${pwdModal.mode === 'reset' ? 'fa-refresh' : 'fa-key'}`}
                    style={{ marginRight: 8, color: pwdModal.mode === 'reset' ? '#f59e0b' : '#0ea5e9' }}
                  />
                  {pwdModal.mode === 'reset' ? 'New Password' : 'User Password'}
                </h5>
                <span style={{ fontSize: 12, color: '#6c757d' }}>
                  {pwdModal.user.name} · @{pwdModal.user.username}
                </span>
              </div>
              <button onClick={() => setPwdModal(null)} style={styles.modalCloseBtn}>
                &times;
              </button>
            </div>
            <div style={{ ...styles.modalBody, paddingTop: 20 }}>
              {pwdModal.loading ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#6c757d' }}>
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: 22 }} /> Loading…
                </div>
              ) : (
                <>
                  {pwdModal.mode === 'reset' && (
                    <div
                      className="alert alert-warning"
                      style={{ fontSize: 13, marginBottom: 14 }}
                    >
                      Old password is no longer valid. Share the new one with the user now.
                    </div>
                  )}
                  <div
                    style={{
                      background: '#0f172a',
                      color: '#22d3ee',
                      padding: '18px 16px',
                      borderRadius: 8,
                      fontFamily: 'monospace',
                      fontSize: 22,
                      letterSpacing: 4,
                      textAlign: 'center',
                      userSelect: 'all',
                      wordBreak: 'break-all',
                    }}
                  >
                    {pwdModal.password || '(empty)'}
                  </div>
                </>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={copyPwdToClipboard}
                disabled={pwdModal.loading || !pwdModal.password}
                style={{ ...styles.btn, ...styles.btnPrimary }}
              >
                <i className="fa fa-copy" /> Copy
              </button>
              {pwdModal.mode === 'show' && !pwdModal.loading && (
                <button
                  onClick={() => resetUserPassword(pwdModal.user)}
                  style={{ ...styles.btn, ...styles.btnSuccess }}
                >
                  <i className="fa fa-refresh" /> Reset to New
                </button>
              )}
              <button
                onClick={() => setPwdModal(null)}
                style={{ ...styles.btn, ...styles.btnOutline }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive CSS injected via style tag */}
      <style>{responsiveCSS}</style>
    </div>
  );
}

// Responsive CSS for hiding columns
const responsiveCSS = `
  @media (max-width: 992px) {
    .hide-tablet { display: none !important; }
  }
  @media (max-width: 768px) {
    .hide-mobile { display: none !important; }
  }
  @media (max-width: 576px) {
    .user-list-search-row {
      flex-direction: column !important;
    }
    .user-list-date-row {
      flex-direction: column !important;
    }
  }
`;

// Inline styles
const styles = {
  pageWrapper: {
    padding: '20px',
    maxWidth: 1400,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a2e',
  },
  pageSubtitle: {
    margin: '4px 0 0',
    fontSize: 14,
    color: '#6c757d',
  },

  // Filter tabs
  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  filterTab: {
    padding: '8px 18px',
    border: '1px solid #dee2e6',
    borderRadius: 8,
    background: '#fff',
    color: '#495057',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
  },
  filterTabActive: {
    background: '#0d6efd',
    color: '#fff',
    borderColor: '#0d6efd',
  },
  tabCount: {
    marginLeft: 8,
    padding: '1px 7px',
    borderRadius: 999,
    background: 'rgba(127,127,127,0.18)',
    fontSize: 11,
    fontWeight: 700,
  },
  roleBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },

  // Card
  card: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e9ecef',
    padding: 0,
    marginBottom: 16,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },

  // Search
  searchRow: {
    display: 'flex',
    gap: 10,
    padding: '16px 20px',
    borderBottom: '1px solid #f1f3f5',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchInputGroup: {
    display: 'flex',
    flex: 1,
    minWidth: 250,
    border: '1px solid #dee2e6',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
    alignItems: 'center',
  },
  searchSelect: {
    border: 'none',
    borderRight: '1px solid #dee2e6',
    padding: '10px 12px',
    fontSize: 13,
    color: '#495057',
    background: '#f8f9fa',
    outline: 'none',
    fontWeight: 500,
    minWidth: 110,
  },
  searchInput: {
    border: 'none',
    padding: '10px 14px',
    fontSize: 14,
    flex: 1,
    outline: 'none',
    minWidth: 150,
  },
  clearBtn: {
    border: 'none',
    background: 'transparent',
    color: '#adb5bd',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: 14,
  },

  // Date filter
  dateRow: {
    display: 'flex',
    gap: 10,
    padding: '12px 20px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  dateLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  dateInput: {
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    outline: 'none',
    color: '#495057',
  },

  // Bulk actions bar
  bulkBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    background: '#e8f4fd',
    borderRadius: 10,
    marginBottom: 16,
    border: '1px solid #b6d4fe',
    flexWrap: 'wrap',
    gap: 10,
  },
  bulkText: {
    fontSize: 14,
    fontWeight: 600,
    color: '#0d6efd',
  },

  // Active search banner
  activeSearchBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    background: '#fff3cd',
    borderRadius: 8,
    marginBottom: 12,
    border: '1px solid #ffc107',
    fontSize: 13,
    flexWrap: 'wrap',
    gap: 8,
  },

  // Table
  tableWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e9ecef',
    background: '#fafbfc',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid #f1f3f5',
    verticalAlign: 'middle',
  },
  tr: {
    transition: 'background 0.15s',
  },
  trSelected: {
    background: '#e8f4fd',
  },
  trDisabled: {
    background: '#fff5f5',
  },

  // User cell
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 160,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a2e',
    lineHeight: 1.3,
  },
  userUsername: {
    fontSize: 12,
    color: '#6c757d',
  },

  // Stats
  statValue: {
    fontSize: 13,
    fontWeight: 600,
    color: '#495057',
  },

  // Badge
  badge: (color) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: color,
    whiteSpace: 'nowrap',
  }),

  // Checkbox
  checkbox: {
    width: 16,
    height: 16,
    cursor: 'pointer',
    accentColor: '#0d6efd',
  },

  // Action buttons
  actionBtns: {
    display: 'flex',
    gap: 4,
    flexWrap: 'nowrap',
    minWidth: 'max-content',
  },
  actionBtn: {
    width: 32,
    height: 32,
    minWidth: 32,
    flexShrink: 0,
    border: '1px solid #dee2e6',
    borderRadius: 6,
    background: '#fff',
    color: '#495057',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
    transition: 'all 0.15s',
    textDecoration: 'none',
  },
  actionBtnDanger: {
    color: '#dc3545',
    borderColor: '#f5c2c7',
  },

  // Buttons
  btn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  },
  btnPrimary: {
    background: '#0d6efd',
    color: '#fff',
  },
  btnSuccess: {
    background: '#198754',
    color: '#fff',
  },
  btnWarning: {
    background: '#ffc107',
    color: '#000',
  },
  btnInfo: {
    background: '#0dcaf0',
    color: '#000',
  },
  btnDanger: {
    background: '#dc3545',
    color: '#fff',
  },
  btnOutline: {
    background: 'transparent',
    color: '#6c757d',
    border: '1px solid #dee2e6',
  },
  btnOutlineSmall: {
    background: 'transparent',
    color: '#856404',
    border: '1px solid #ffc107',
    padding: '4px 12px',
    fontSize: 12,
  },

  // Loading / Error / Empty
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  errorState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },

  // Pagination
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 20px',
    borderTop: '1px solid #e9ecef',
    flexWrap: 'wrap',
    gap: 12,
  },
  paginationInfo: {
    fontSize: 13,
    color: '#6c757d',
  },
  pagination: {
    display: 'flex',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    gap: 4,
    flexWrap: 'wrap',
  },
  pageLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 500,
    color: '#0d6efd',
    background: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: 6,
    textDecoration: 'none',
    gap: 4,
    minWidth: 36,
    height: 36,
    transition: 'all 0.15s',
  },
  pageLinkActive: {
    background: '#0d6efd',
    color: '#fff',
    borderColor: '#0d6efd',
  },
  pageLinkDisabled: {
    color: '#adb5bd',
    pointerEvents: 'none',
    opacity: 0.6,
  },
  pageEllipsis: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 6px',
    color: '#adb5bd',
    fontSize: 14,
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 20,
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef',
  },
  modalAvatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  modalCloseBtn: {
    border: 'none',
    background: '#f1f3f5',
    width: 36,
    height: 36,
    borderRadius: '50%',
    fontSize: 22,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#495057',
    lineHeight: 1,
  },
  modalBody: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
  },
  modalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
  },
  modalField: {
    padding: '8px 0',
  },
  modalFieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  modalFieldValue: {
    fontSize: 14,
    color: '#1a1a2e',
    fontWeight: 500,
  },
  modalFooter: {
    display: 'flex',
    gap: 8,
    padding: '16px 24px',
    borderTop: '1px solid #e9ecef',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
};


// ===========================================================================
// Fingerprint enrolment
// ===========================================================================
function FingerprintModal({ user, token, onClose }) {
  const [status, setStatus] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [enrollment, config] = await Promise.all([
        axios.get(`${api}/api/fingerprint/status/${user._id}`, { headers }),
        axios.get(`${api}/api/fingerprint/config`, { headers }),
      ]);
      setStatus(enrollment.data);
      setBridge(config.data);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user._id, token]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!window.confirm(`Remove the fingerprint enrolment for ${user.username}?`)) return;
    try {
      const { data } = await axios.delete(`${api}/api/fingerprint/${user._id}`, { headers });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <div>
            <h4>
              <i className="fa fa-hand-paper-o" style={{ marginRight: 8, color: '#7c3aed' }} />
              Fingerprint
            </h4>
            <p className="sub">{user.name} · @{user.username}</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          {loading ? (
            <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading…</div>
          ) : (
            <>
              {bridge?.simulationMode && (
                <div className="ops-note warn" style={{ marginBottom: 14 }}>
                  <i className="fa fa-flask" />
                  <span>
                    <strong>Simulation mode is on.</strong> Enrolments captured now are placeholders,
                    not real fingerprints. Re-enrol once the SLK20R bridge is installed.
                  </span>
                </div>
              )}

              {status?.enrolled ? (
                <>
                  <div className="ops-note ok" style={{ marginBottom: 14 }}>
                    <i className="fa fa-check-circle" />
                    <span>
                      {status.fingers} finger{status.fingers === 1 ? '' : 's'} on file
                      {status.lastMatchedAt &&
                        ` · last matched ${new Date(status.lastMatchedAt).toLocaleString()}`}
                    </span>
                  </div>
                  <table className="ops-table" style={{ minWidth: 0 }}>
                    <thead>
                      <tr><th>Finger</th><th>Quality</th><th>Enrolled</th></tr>
                    </thead>
                    <tbody>
                      {status.details.map((finger) => (
                        <tr key={finger.index}>
                          <td>
                            {finger.label}
                            {finger.simulated && (
                              <span className="ops-badge amber" style={{ marginLeft: 6 }}>simulated</span>
                            )}
                          </td>
                          <td>{finger.quality || '—'}</td>
                          <td style={{ fontSize: 12, color: 'var(--ops-muted)' }}>
                            {new Date(finger.enrolledAt).toLocaleDateString()}
                            {finger.enrolledByName && ` by ${finger.enrolledByName}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="ops-note info">
                  <i className="fa fa-info-circle" />
                  <span>
                    No fingerprint on file. Without one, this customer cannot pass the fingerprint
                    check at the delivery counter unless a staff member overrides it.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="ops-modal-foot">
          <button className="ops-btn" onClick={onClose}>Close</button>
          {status?.enrolled && (
            <button className="ops-btn danger" onClick={remove}>
              <i className="fa fa-trash" /> Remove
            </button>
          )}
          {!loading && (
            <button className="ops-btn primary" onClick={() => setEnrolling(true)}>
              <i className="fa fa-hand-paper-o" /> {status?.enrolled ? 'Re-enrol' : 'Enrol fingerprint'}
            </button>
          )}
        </div>

        {enrolling && (
          <EnrollModal
            user={user}
            bridge={bridge}
            headers={headers}
            onClose={() => setEnrolling(false)}
            onDone={() => { setEnrolling(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}
